import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { PLACE_SUBSCRIPTION_TYPE } from '@/constants';
import { persistResolvedLocationForAnonymous } from '@/features/searchV2/components/AutoSuggest/LocationAutoSuggest/LocationAutoSuggest.helpers';
import { SEARCH_RESULT_PER_PAGE_LIMIT } from '@/features/searchV2/constants';
import { getSearchPerformedTrackingProps, trackSearchPerformed } from '@/features/searchV2/helpers/tracking';
import { SearchContext } from '@/features/searchV2/types/tracking';
import { promiseWrapper, server, url } from '@/helpers';
import { trackMpEvent } from '@/helpers/analyticsTS';
import {
  collectBiddingBidIds,
  filterOnlyRequiredSearchParams,
  nextProfilePages,
  trackSearchResultsCustomerIO,
  withNearMeDefaultSort,
} from '@/hooks/search/useSearch.helpers';
import { _s } from '@/locale';
import { SearchState, SEARCH_ACTION } from '@/reducers/searchV2Reducer';
import {
  companiesAction,
  SerpTabCounts,
  UnifiedSearchAction,
  UNIFIED_SEARCH_ACTION,
} from '@/reducers/unifiedSearchReducer';
import { placeService } from '@/services';
import { Tracking } from '@/types/analytics';
import { resolvedLocationSchema } from '@/types/api/services/search';
import { UrlSearchState } from '@/types/state/search';
import { toast } from 'react-toastify';
import { z } from 'zod';

const tabCountsResponseSchema = z.object({
  sistaminuten: z.number().int().nonnegative().optional(),
});

export function getCompaniesRequestKey(urlState: UrlSearchState, bounds: string | undefined): string {
  const { page, ...params } = urlState;
  return JSON.stringify({ ...params, bounds: bounds ?? null });
}

/** The Companies total follows the search and filters, never the sort or page. */
export function getCompaniesCountKey(requestKey: string | undefined): string | undefined {
  if (requestKey === undefined) return undefined;
  const { sort, ...params } = JSON.parse(requestKey);
  return JSON.stringify(params);
}

/** Premium profile places sit on the list's last page, so they count only when the first page is the last. */
export function getCompaniesCount(results: number, profilePages: SearchState['profilePages'] | undefined): number {
  if (results > SEARCH_RESULT_PER_PAGE_LIMIT) return results;
  const premium = (profilePages ?? []).filter(
    (place) => place.settings?.subscriptionType === PLACE_SUBSCRIPTION_TYPE.PROFILE_PAGE_PREMIUM,
  );
  return results + premium.length;
}

/** Counts describe the search (query, place, map area, dates), never the Companies filters or sort. */
export function getCountsKey(urlState: UrlSearchState, bounds: string | undefined): string {
  const { q, location, lat, lon, startDate, endDate } = urlState;
  return JSON.stringify({ q, location, lat, lon, startDate, endDate, bounds: bounds ?? null });
}

function withoutDates(countsKey: string): string {
  const { startDate, endDate, ...search } = JSON.parse(countsKey);
  return JSON.stringify(search);
}

/** A tab that sends no dates takes the latest counts held for the same search with any dates. */
export function findSearchCounts(
  countsBySearch: Record<string, SerpTabCounts>,
  countsKey: string,
  { anyDates }: { anyDates: boolean },
): SerpTabCounts | undefined {
  if (!anyDates) return countsBySearch[countsKey];
  const search = withoutDates(countsKey);
  const heldKey = Object.keys(countsBySearch)
    .reverse()
    .find((key) => withoutDates(key) === search);
  return heldKey === undefined ? undefined : countsBySearch[heldKey];
}

export function getTabCountTrackingProps(counts: SerpTabCounts) {
  return { sistaminuten_result_count: counts.sistaminuten };
}

export function trackCompaniesSearchPerformed({
  urlState,
  searchState,
  context,
  screenName,
  counts,
}: {
  urlState: UrlSearchState;
  searchState: SearchState;
  context: SearchContext;
  screenName: Tracking.ScreenName;
  counts: SerpTabCounts;
}) {
  trackSearchPerformed({
    ...getSearchPerformedTrackingProps({ urlState, searchState, context, screenName }),
    ...getTabCountTrackingProps(counts),
  });
}

export async function requestCompanies(
  {
    params,
    state,
    requestKey,
    resetAllSearchItems,
    keywordSubmitted,
    searchContext,
    screenName,
    countsKey,
    counts,
    isCurrentRequest,
  }: {
    params: UrlSearchState;
    state: SearchState;
    requestKey: string;
    resetAllSearchItems: boolean;
    keywordSubmitted?: boolean;
    searchContext?: SearchContext;
    screenName: Tracking.ScreenName;
    countsKey: string;
    /** The counts already held for this search, or undefined to ask the backend for them. */
    counts: SerpTabCounts | undefined;
    isCurrentRequest: () => boolean;
  },
  dispatch: (action: UnifiedSearchAction) => void,
) {
  params = withNearMeDefaultSort('default', params);

  const requestParams = {
    ...filterOnlyRequiredSearchParams({ ...params, bounds: state.bounds, keywordSubmitted }),
    ...(counts ? {} : { includeCounts: 1 }),
  };

  dispatch(companiesAction({ type: SEARCH_ACTION.SET_FETCHING, payload: true }));

  const { data: response, error } = await promiseWrapper(
    server.request.get('/search/new', { params: requestParams, paramsSerializer: url.serialize }),
  );

  if (!isCurrentRequest()) return;

  if (error) {
    toast(
      ({ closeToast }) => (
        <Snackbar type="danger" label={_s('serverError')} action={<button onClick={closeToast}>OK</button>} />
      ),
      { autoClose: 3000 },
    );
    dispatch({ type: UNIFIED_SEARCH_ACTION.COMPANIES_FAILED, payload: { requestKey } });

    trackMpEvent({
      name: 'error_shown',
      properties: {
        screen_name: screenName,
        error_code: error?.response?.status ? `http_${error?.response?.status}` : undefined,
        error_type: 'search_error',
      },
    });

    return;
  }

  const resolvedLocation = resolvedLocationSchema.safeParse(response?.data?.resolvedLocation).success
    ? response.data.resolvedLocation
    : undefined;

  if (resolvedLocation) {
    persistResolvedLocationForAnonymous(resolvedLocation);
  }

  const {
    hits,
    topSearch = {},
    places,
    profilePages,
    version,
    extendSearchIteration,
    showInjectionNotice = false,
    debugLocation = undefined,
    boundsFit = undefined,
    isIndexable = undefined,
  } = response.data;
  const biddingBidIds = collectBiddingBidIds(topSearch);

  if (biddingBidIds.length > 0) {
    placeService.storeImpressions(biddingBidIds);
  }

  const page = params.page ?? 0;
  const results: number = hits?.value || 0;

  const payload: Partial<SearchState> = {
    places: resetAllSearchItems ? { [page]: [...(places || [])] } : { ...state.places, [page]: [...(places || [])] },
    results,
    topSearch: topSearch || [],
    profilePages: nextProfilePages(state.profilePages, profilePages, {
      resetAllSearchItems,
      page: Number(page) || 0,
      results,
    }),
    showInjectionNotice,
    extendSearchIteration: !isNaN(extendSearchIteration) ? extendSearchIteration : 0,
    isSistaminutenSearch: false,
    version,
    debugLocation,
    boundsFit,
    isIndexable,
  };

  const receivedCounts = tabCountsResponseSchema.safeParse(response.data?.counts);
  const tabCounts: SerpTabCounts = {
    ...(counts ?? (receivedCounts.success ? receivedCounts.data : {})),
    places: getCompaniesCount(results, profilePages),
  };
  dispatch({ type: UNIFIED_SEARCH_ACTION.SET_COUNTS, payload: { counts: tabCounts, countsKey } });

  const _searchContext = searchContext ?? state.searchContext ?? 'free_search';

  trackSearchResultsCustomerIO(params, { ...state, ...payload } as SearchState);

  trackCompaniesSearchPerformed({
    urlState: params,
    searchState: payload as SearchState,
    context: _searchContext,
    screenName,
    counts: tabCounts,
  });

  payload.searchContext = _searchContext === 'navigation' || _searchContext === 'tab' ? 'free_search' : _searchContext;

  dispatch({ type: UNIFIED_SEARCH_ACTION.COMPANIES_LOADED, payload: { results: payload, requestKey } });
}

export async function requestCompaniesExtended(
  {
    params,
    state,
    countsKey,
    counts,
    isCurrentRequest,
  }: {
    params: UrlSearchState;
    state: SearchState;
    countsKey: string;
    counts: SerpTabCounts | undefined;
    isCurrentRequest: () => boolean;
  },
  dispatch: (action: UnifiedSearchAction) => void,
) {
  params = withNearMeDefaultSort('default', params);
  const page = Number(params.page) || 0;
  const { extendSearchIteration = 0 } = state;
  const requestParams = {
    ...filterOnlyRequiredSearchParams({ ...params }),
    extend: true,
    limit: SEARCH_RESULT_PER_PAGE_LIMIT - state.places?.[page]?.length || 0,
    extendSearchIteration: !isNaN(extendSearchIteration) ? extendSearchIteration + 1 : 0,
  };

  dispatch(companiesAction({ type: SEARCH_ACTION.SET_EXTEND_FETCHING, payload: true }));

  const { data: response, error } = await promiseWrapper(
    server.request.get('/search/new', { params: requestParams, paramsSerializer: url.serialize }),
  );

  if (!isCurrentRequest()) {
    dispatch(companiesAction({ type: SEARCH_ACTION.SET_EXTEND_FETCHING, payload: false }));
    return;
  }

  if (error) {
    const stopFetching = () => dispatch(companiesAction({ type: SEARCH_ACTION.SET_EXTEND_FETCHING, payload: false }));
    toast(
      ({ closeToast }) => (
        <Snackbar
          type="danger"
          label={_s('serverError')}
          action={
            <button
              onClick={() => {
                closeToast();
                stopFetching();
              }}>
              OK
            </button>
          }
        />
      ),
      { onClose: stopFetching },
    );
    return;
  }

  const { places: extendedPlaces = [] } = response.data;
  const results = extendedPlaces.length + state.results;

  dispatch(companiesAction({ type: SEARCH_ACTION.SET_EXTENDED_RESULTS, payload: { page, extendedPlaces, results } }));
  dispatch({
    type: UNIFIED_SEARCH_ACTION.SET_COUNTS,
    payload: { counts: { ...counts, places: getCompaniesCount(results, state.profilePages) }, countsKey },
  });
}
